import React, { useEffect, useState } from "react";

import { AxiosError } from "axios";
import { toast } from "react-toastify";

import { Box, Button, Divider, Fab, FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField } from "@mui/material";
import { CheckCircle, Delete, Info, RemoveCircle, Search } from "@mui/icons-material";
import { Field, Form, Formik } from "formik";

import { Analysis } from "../../../../../app/models/general/analysis";
import { PaginatedResult } from "@/app/models/default/paginated-result";

import { BaseService } from "../../../../services/base-service";
import { URLS } from "../../../../services/app-urls";

import { ErrorComponent } from "../../../../components/error/error.component";
import TablePaginationComponent from "../../../../components/table/table-pagination.component";
import WarningTooltip from "../../../../components/tooltip/warning-tooltip";
import DialogVersion from "../../../../components/dialog/dialog.component";
import Loading from "../../../../components/loading/loading";

import { translate } from "../../../../translate/translate";
import { parseLocaleNumber } from "../../../../utils/locale-number-utils";
import { Stock } from "@/app/models/general/stock";
import EditIcon from "@mui/icons-material/Edit";

type EditableColumn = "on_hand";

const SimulationStockComponent = ({ analysis }: { analysis: Analysis }) => {
  const service = new BaseService<Stock>(URLS.STOCK);
  const analysisService = new BaseService(URLS.ANALYSIS);

  const [stockComponents, setStockComponents] = useState<Stock[]>([]);
  const [searchStock, setSearchStock] = useState("");
  const [pagination, setPagination] = useState<PaginatedResult<any>>();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);
  const [openResetModal, setOpenResetModal] = useState<boolean>(false);

  const [editingCell, setEditingCell] = useState<{ cellKey: string } | null>(null);

  const [filterFinalProduct, setFilterFinalProduct] = useState("all");
  const [filterInTest, setFilterInTest] = useState("all");
  const [loading, setLoading] = useState<boolean>(false);

  const displayedColumns = ["part_number", "description", "quantity", "is_final_product", "is_in_test"];
  const displayedColumnsShow = ["part_number", "description", "on_hand", "is_final_product", "is_in_test"];
  const editableColumns = ["quantity"];
  const rightAlignColumns = ["on_hand"];
  const readOnlyBooleanFieldList = ["is_final_product", "is_in_test"];
  const searchFieldLabel = [translate.t("part_number")];

  const handleSearchChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setSearchStock(event.target.value as string);
  };

  const search = () => {
    service.clearParameters();
    service.addParameter("analysis_id", analysis.id);
    if (searchStock) service.addParameter("search", searchStock);
    if (filterFinalProduct !== "all")
      service.addParameter("is_final_product", filterFinalProduct === "true");
    if (filterInTest !== "all")
      service.addParameter("is_in_test", filterInTest === "true");

    service.addParameter("limit", rowsPerPage);
    service.addParameter("offset", page * rowsPerPage);

    setLoading(true);
    service
      .getAllPaginate()
      .then((response) => {
        setPagination(response.data);
        setStockComponents(response.data.results);
      })
      .catch((error: any) => {
        ErrorComponent(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    search();
  }, []);

  useEffect(() => {
    search();
  }, [page, rowsPerPage, filterFinalProduct, filterInTest]);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenResetModal = () => {
    setOpenResetModal(true);
  };

  const handleCloseResetModal = (result: unknown) => {
    if (typeof result === "boolean" && result && analysis) {
      analysisService.clearParameters();
      analysisService.deleteFromDetailRoute(analysis?.id, "reset-stock")
        .then(() => {
          toast.success(translate.t("successfully_reset"));
          search();
        });
    }
    setOpenResetModal(false);
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
  };

  const handleConfirmEdit = (currentCell: string, column: EditableColumn, row: Stock) => {
    const value = (document.querySelector(`#cell-input-${currentCell}`) as HTMLInputElement)?.value;
    handleSaveField(row.id, value, column);
  };

  const handleKeyDownSave = (e: any, column: EditableColumn, row: Stock) => {
    handleSaveField(row.id, (e.target as HTMLInputElement).value, column);
  };

  const handleCellClick = (column: EditableColumn, row: Stock | any) => {
    const safePartNumber = row.part_number.replace(/\./g, "");
    const safeColumn = column.replace(/\//g, "-");
    const cellKey = `${safePartNumber}-${safeColumn}`;
    setEditingCell({ cellKey: cellKey });
  };

  const renderTextField = (currentCell: string, column: EditableColumn, row: Stock) => {
    const { inputType, textAlign, effectiveValue } = getColumnConfig(column, row);

    const handleNumericChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      let v = event.target.value;
      const lang = translate.language === "en" ? "en" : "pt";

      if (lang === "en") {
        v = v.replace(/[^\d.]/g, "");

        const parts = v.split(".");
        let intPart = parts[0].replace(/^0+(?!$)/, "");
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        v = intPart;
        if (parts[1] !== undefined) {
          v += "." + parts[1].replace(/,/g, "");
        }
      } else {
        v = v.replace(/[^\d,]/g, "");

        const parts = v.split(",");
        let intPart = parts[0].replace(/^0+(?!$)/, "");
        intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        v = intPart;
        if (parts[1] !== undefined) {
          v += "," + parts[1].replace(/\./g, "");
        }
      }

      event.target.value = v;
    };

    return (
      <TextField
        id={`cell-input-${currentCell}`}
        autoFocus
        defaultValue={parseLocaleNumber(effectiveValue, false)}
        variant="standard"
        fullWidth
        size="small"
        type={inputType}
        inputProps={textAlign ? { style: { textAlign: textAlign as any } } : undefined}
        onChange={handleNumericChange}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            handleKeyDownSave(e, column, row)
          }
        }}
      />
    );
  };

  const handleSaveField = async (stockComponentId: number, newValue: any, _: EditableColumn) => {
    const originalStockComponents = [...stockComponents];
    const currentRow: any = stockComponents.find((row: any) => row.id === stockComponentId);
    if (!currentRow) return;

    const lang = translate.language === "en" ? "en" : "pt";
    let numericString = newValue;

    if (typeof numericString === "string") {
      if (lang === "en") {
        numericString = numericString.replace(/,/g, "");
      } else {
        numericString = numericString.replace(/\./g, "").replace(",", ".");
      }
    }

    let parsedValue = typeof numericString === "string" ? parseFloat(numericString) : numericString;
    parsedValue = isNaN(parsedValue) ? 0 : parsedValue;

    setStockComponents(prev =>
      prev.map((row: any) =>
        row.id === stockComponentId
          ? {
            ...row,
            effective_quantity: parsedValue,
          }
          : row,
      ),
    );

    setEditingCell(null);

    try {
      await analysisService.saveFromDetailRoute(
        { component_id: stockComponentId, quantity: parsedValue },
        analysis?.id,
        "update-stock",
      );
      toast.success(translate.t("successfully_updated"));
    } catch (err) {
      setStockComponents(originalStockComponents);
      ErrorComponent(err as AxiosError);
      toast.error(translate.t("update_failed"));
    }
  };

  const getColumnConfig = (column: string, row: any) => {
    const columnConfigs = {
      quantity: {
        isEdited: row?.effective_quantity !== null && row?.effective_quantity !== row?.original_quantity,
        originalValue: parseLocaleNumber(row?.original_quantity, false),
        effectiveValue: row?.effective_quantity ?? row?.original_quantity,
        formattedValue: parseLocaleNumber(row?.effective_quantity ?? row?.original_quantity ?? 0, false),
        inputType: "text" as const,
        textAlign: "right" as const,
        fontWeight: row?.effective_quantity !== null && row?.effective_quantity !== row?.original_quantity ? 600 : 400,
      },
    };

    return columnConfigs[column as keyof typeof columnConfigs];
  };

  const renderReadOnlyCell = (column: string, row: any) => {
    return <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      {
        row[column] ? (
          <CheckCircle sx={{ color: "#00B894" }} />
        ) : (
          <RemoveCircle sx={{ color: "#90A4AE" }} />
        )
      }
    </Box>;
  };

  const renderEditableCell = (
    column: EditableColumn,
    row: Stock,
  ) => {
    const safeColumn = column.replace(/\//g, "-");
    const cellKey = `${(row.part_number ?? "").replace(/\./g, "")}-${safeColumn}`;
    const isEditing = editingCell?.cellKey === cellKey;

    const {
      isEdited,
      originalValue,
      formattedValue,
      fontWeight,
    } = getColumnConfig(column, row);

    if (isEditing) {
      return (
        <Box
          display="flex"
          alignItems="center"
          width={"100%"}
          gap={0.5}
        >
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={handleCancelEdit}
            sx={{ borderColor: "#cfd8dc" }}
          >
            ✕
          </Button>

          <Button
            size="small"
            color="success"
            variant="outlined"
            onClick={() => handleConfirmEdit(cellKey, column, row)}
            sx={{ borderColor: "#cfd8dc" }}
          >
            ✓
          </Button>

          <Box flex={1}>
            {renderTextField(cellKey, column, row)}
          </Box>
        </Box>
      );
    }

    return (
      <Box
        onClick={() => handleCellClick(column, row)}
        sx={{
          cursor: "pointer",
          fontWeight: fontWeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "4px",
          textAlign: "right",
        }}
      >
        {!isEdited && (
          <EditIcon
            className="eye-icon"
            sx={{ opacity: 0, transition: "opacity 0.2s", color: "#90A4AE" }}
            fontSize="small"
          />
        )}
        {isEdited && (
          <WarningTooltip
            color="var(--blue-gray-300)"
            messageKey={"original_value"}
            additionalText={": " + originalValue}
            IconComponent={Info}
          />
        )}
        {formattedValue}
      </Box>
    );
  };

  const renderCellHandler = (column: string, _index: number, row: any) => {
    if (editableColumns.includes(column)) {
      return renderEditableCell(column as EditableColumn, row);
    }
    if (readOnlyBooleanFieldList.includes(column)) {
      return renderReadOnlyCell(column, row);
    }
    return row[column];
  };

  const renderCellHandlers: Record<string, typeof renderCellHandler> = {};
  displayedColumns.forEach((col) => {
    renderCellHandlers[col] = renderCellHandler;
  });

  const headerCellCustomStyleHandler = (column: string) => {
    if (rightAlignColumns.includes(column)) {
      return {
        textAlign: "right",
      };
    }

    if (readOnlyBooleanFieldList.includes(column)) {
      return {
        textAlign: "center",
      };
    }
  };

  const cellCustomStyleHandler = (column: string) => {
    if (column === "quantity") {
      return {
        cursor: "pointer",
        color: "#3949AB",
        textAlign: "right",
        padding: "8px",
        "&:hover": {
          "& .eye-icon": {
            opacity: 1,
          },
        },
      };
    }
  };

  return (
    <section>
      <Loading open={loading} />
      <Paper variant="outlined" sx={{ padding: "16px", marginBottom: "16px" }}>
        <Formik
          enableReinitialize={true}
          initialValues={{ searchValue: "" }}
          onSubmit={(_, { setSubmitting }) => {
            if (page === 0) {
              search();
            } else {
              setPage(0);
            }
            setSubmitting(false);
          }}
        >
          {({ isSubmitting }) => (
            <Form autoComplete="off">
              <Grid container spacing={2} alignItems="center">

                <Grid item xs>
                  <Grid container spacing={2}>
                    <Grid item xs>
                      <Field
                        id="inp-search-material"
                        name="searchValue"
                        variant="outlined"
                        label={searchFieldLabel}
                        as={TextField}
                        value={searchStock}
                        onChange={handleSearchChange}
                        fullWidth
                      />
                    </Grid>

                    <Grid item xs>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="filter-final-product-label">
                          {translate.t("is_final_product")}
                        </InputLabel>
                        <Select
                          labelId="filter-final-product-label"
                          value={filterFinalProduct}
                          onChange={(e) => {
                            setFilterFinalProduct(e.target.value);
                            setPage(0);
                          }}
                          label="Produto Final?"
                        >
                          <MenuItem value="all">{translate.t("all")}</MenuItem>
                          <MenuItem value="true">{translate.t("yes")}</MenuItem>
                          <MenuItem value="false">{translate.t("no")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs>
                      <FormControl fullWidth variant="outlined">
                        <InputLabel id="filter-in-test-label">
                          {translate.t("is_in_test")}
                        </InputLabel>
                        <Select
                          labelId="filter-in-test-label"
                          value={filterInTest}
                          onChange={(e) => {
                            setFilterInTest(e.target.value);
                            setPage(0);
                          }}
                          label="Em Teste?"
                        >
                          <MenuItem value="all">{translate.t("all")}</MenuItem>
                          <MenuItem value="true">{translate.t("yes")}</MenuItem>
                          <MenuItem value="false">{translate.t("no")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid item>
                  <Fab size="small" type="submit" color="primary" disabled={isSubmitting}>
                    <Search id="submit-search-po-material" />
                  </Fab>
                </Grid>

                <Grid item>
                  <Divider orientation="vertical" sx={{ height: 40 }} />
                </Grid>

                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleOpenResetModal}
                    startIcon={<Delete height={18} width={18} />}
                  >
                    {translate.t("reset")}
                  </Button>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>

        <Grid item>
          <Divider
            orientation="horizontal"
            variant="fullWidth"
            sx={{ marginTop: "20px" }}
          />
        </Grid>

        <Grid sx={{ marginTop: "20px" }}>
          <TablePaginationComponent
            state={stockComponents}
            displayedColumnsShow={displayedColumnsShow}
            displayedColumns={displayedColumns}
            readOnlyBooleanFieldList={readOnlyBooleanFieldList}
            count={pagination?.count}
            next={pagination?.next}
            previous={pagination?.previous}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            headerCellCustomStyleHandler={headerCellCustomStyleHandler}
            cellCustomStyleHandler={cellCustomStyleHandler}
            renderCellHandler={renderCellHandlers}
            columnStyles={{ 1: "15%", 2: "40%", 3: "15%", 4: "15%", 5: "15%" }}
            stickyHeader
            disablePointer
          />
        </Grid>
      </Paper>

      {openResetModal && (
        <DialogVersion
          id="dialog-reset-stock"
          title={translate.t("reset_data")}
          message={translate.t("confirm_reset")}
          labelButton={translate.t("confirm")}
          colorButton={"primary"}
          open={openResetModal}
          onClose={handleCloseResetModal}
        />
      )}
    </section>
  );
};

export default SimulationStockComponent;
